/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './line-item-template-copy-modal.component.html';
import {Uuid} from '../../components/util/uuid';
import {lineItemModDates, setLineItemDefaults} from './line-item-commons';
import {LineItemTemplateResource} from '../../components/api/resources/resource/line-item.resource';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

export interface LineItemTemplateCopyResult {
  newId: string;
  destination: boolean;
  copyToLineItem: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-line-item-template-copy-modal',
  template
})
export class LineItemTemplateCopyModalComponent implements OnInit {
  model: any;
  copyToLineItem = false;
  fromLineItemList = false;

  readonly onCopied = new Subject<LineItemTemplateCopyResult>();

  lineItem: any = {};
  lineItemTemplate: any;
  allTemplates: any[] = [];
  templatesResolved = false;
  isMarketplace = false;
  isNew = true;
  updating = false;
  copy = {targeting: true, creatives: true};
  destination = true;
  disabled = false;
  validationErrors: any[] = [];

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(LineItemTemplateResource) private readonly LineItemTemplate: any,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.isMarketplace = LocalNetworkProfile.isMarketplacePlatform();
  }

  ngOnInit(): void {
    if (this.fromLineItemList) {
      // Search does not index line item templates; load via the resource API.
      this.LineItemTemplate.query((data: any) => {
        deferMarkForCheck(this.cdr, () => {
          this.allTemplates = data.results || [];
          this.templatesResolved = true;
        });
      });
    } else {
      this.assignInfo(this.model);
    }
  }

  templateChanged(): void {
    const templateId = _.get(this.lineItemTemplate, 'id');
    if (!templateId) {
      return;
    }
    // List results can be incomplete; fetch the full template before copy fields.
    this.updating = true;
    this.LineItemTemplate.get({id: templateId}).$promise.then((data: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.lineItemTemplate = data;
        this.assignInfo(data);
        this.updating = false;
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.assignInfo(this.lineItemTemplate);
        this.updating = false;
      });
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  copyCampaign(): void {
    if (!this.lineItem) {
      return;
    }
    this.disabled = true;
    this.validationErrors = [];
    this.adnModalLoading.applyModalLoading();
    const newId = Uuid.generate();
    const method = this.copyToLineItem ? 'copyAsLineItem' : 'copyLineItemTemplate';
    lineItemModDates(this.adnTimeShifter, this.lineItem, this.lineItem.modStartDate, this.lineItem.modEndDate);
    const copyRequest = this.LineItemTemplate[method](this.lineItem, newId, this.copy.targeting);
    Promise.resolve(copyRequest.$promise || copyRequest).then(() => {
      deferMarkForCheck(this.cdr, () => {
        this.disabled = false;
        this.adnModalLoading.removeModalLoading();
        completeCopyModal(this.bsModalRef, this.onCopied, {
          destination: this.destination,
          newId,
          copyToLineItem: this.copyToLineItem
        });
      });
    }, (errors: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.disabled = false;
        this.validationErrors = _.get(errors, ['data', 'errors'], []) || [];
        this.adnModalLoading.removeModalLoading();
      });
    });
  }

  private assignInfo(data: any): void {
    if (!data) {
      return;
    }
    this.lineItem = _.cloneDeep(data);
    this.lineItem.name = data.name + ' - derived from line item template';
    setLineItemDefaults(this.LocalNetworkProfile, this, this.lineItem);
    if (this.lineItem.startDate) {
      this.lineItem.modStartDate = this.lineItem.startDate;
    }
    if (this.lineItem.endDate) {
      this.lineItem.modEndDate = this.lineItem.endDate;
    }
  }
}
